import { useCallback, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import { firestoreCollection } from 'util/constants';

// Read-only figures for the Admin Dashboard. Everything here is a one-off
// .get() — no listeners and no writes. Sizes are stored in bytes, sometimes as
// strings, so they are coerced with Number().

export type CategoryStat = { files: number; bytes: number };

export type ProjectStorage = { id: string; title: string; client: string; bytes: number; files: number };

export type AdminUser = { id: string; email: string; isAdmin: boolean };

export type AdminStats = {
  projects: { total: number; complete: number; incomplete: number; noClient: number };
  records: { total: number; withoutFiles: number };
  videos: { total: number; bytes: number; byCategory: Record<string, CategoryStat> };
  assets: CategoryStat;
  gallery: CategoryStat;
  images: number;
  notes: number;
  totalBytes: number;
  topProjects: ProjectStorage[];
  users: AdminUser[];
  loadedAt: Date;
};

const toBytes = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getAll = async (collection: string) => (await firebase.firestore().collection(collection).get()).docs;

const useAdminStats = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [projects, records, videos, assets, gallery, images, notes, users] = await Promise.all([
        getAll(firestoreCollection.projects),
        getAll(firestoreCollection.records),
        getAll(firestoreCollection.videos),
        getAll(firestoreCollection.assets),
        getAll(firestoreCollection.gallery),
        getAll('images'),
        getAll(firestoreCollection.notes),
        getAll(firestoreCollection.users),
      ]);

      // Projects
      const projectInfo: Record<string, { title: string; client: string }> = {};
      let complete = 0;
      let noClient = 0;
      projects.forEach((doc) => {
        const p = doc.data();
        if (p.complete === true) complete++;
        if (!p.client || !String(p.client).trim()) noClient++;
        projectInfo[doc.id] = { title: p.title || '(untitled)', client: p.client || '' };
      });

      // Records → project lookup, so video sizes can be rolled up per project
      const recordProject: Record<string, string> = {};
      records.forEach((doc) => {
        recordProject[doc.id] = doc.data().projectID;
      });

      // Videos
      const byCategory: Record<string, CategoryStat> = {};
      const perProject: Record<string, { bytes: number; files: number }> = {};
      const recordsWithFiles = new Set<string>();
      let videoBytes = 0;
      videos.forEach((doc) => {
        const v = doc.data();
        const bytes = toBytes(v.size);
        const category = v.category || 'uncategorised';
        videoBytes += bytes;
        byCategory[category] = byCategory[category] || { files: 0, bytes: 0 };
        byCategory[category].files++;
        byCategory[category].bytes += bytes;
        if (v.recordID) recordsWithFiles.add(v.recordID);
        const projectId = recordProject[v.recordID];
        if (projectId) {
          perProject[projectId] = perProject[projectId] || { bytes: 0, files: 0 };
          perProject[projectId].bytes += bytes;
          perProject[projectId].files++;
        }
      });

      const sumFiles = (docs: firebase.firestore.QueryDocumentSnapshot[]): CategoryStat =>
        docs.reduce(
          (acc, doc) => {
            const d = doc.data();
            const bytes = toBytes(d.size);
            acc.files++;
            acc.bytes += bytes;
            if (d.projectID) {
              perProject[d.projectID] = perProject[d.projectID] || { bytes: 0, files: 0 };
              perProject[d.projectID].bytes += bytes;
              perProject[d.projectID].files++;
            }
            return acc;
          },
          { files: 0, bytes: 0 } as CategoryStat,
        );
      const assetStat = sumFiles(assets);
      const galleryStat = sumFiles(gallery);

      const topProjects: ProjectStorage[] = Object.keys(perProject)
        .map((id) => ({
          id,
          title: projectInfo[id]?.title || '(deleted project)',
          client: projectInfo[id]?.client || '',
          ...perProject[id],
        }))
        .sort((a, b) => b.bytes - a.bytes)
        .slice(0, 10);

      setStats({
        projects: { total: projects.length, complete, incomplete: projects.length - complete, noClient },
        records: { total: records.length, withoutFiles: records.filter((r) => !recordsWithFiles.has(r.id)).length },
        videos: { total: videos.length, bytes: videoBytes, byCategory },
        assets: assetStat,
        gallery: galleryStat,
        images: images.length,
        notes: notes.length,
        totalBytes: videoBytes + assetStat.bytes + galleryStat.bytes,
        topProjects,
        users: users
          .map((doc) => ({ id: doc.id, email: doc.data().email || '(no email)', isAdmin: doc.data().isAdmin === true }))
          .sort((a, b) => a.email.localeCompare(b.email)),
        loadedAt: new Date(),
      });
    } catch (e) {
      console.error('Admin stats failed to load:', e);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { stats, loading, error, reload: load };
};

export const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
};

export default useAdminStats;
